import React, { useEffect } from 'react'
import { Collapse } from 'react-bootstrap'
import _cloneDeep from 'lodash/cloneDeep'
import styles from '../../../../Styles/index.module.scss'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { generateAriaId } from '../../../../../../ActionsBar/utils/generateAriaId'
import { useExpand } from '../../../../../../../hooks/useExpand'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import { DuplicateBtn } from '../../../../../../buttons/DuplicateBtn/DuplicateBtn'
import AppButton from '../../../../../../buttons/AppButton/AppButton'
import { generateElementID } from '../../../../../../../utils/generateElementID'
import { ScheduleDates } from '../../../SchedulesContainer/ScheduleDates'
import { ScheduleDaysOfWeek } from '../../../SchedulesContainer/ScheduleDaysOfWeek'
import { ScheduleTimes } from '../../../SchedulesContainer/ScheduleTimes'
import { ScheduleBlockSummary } from '../../../SchedulesContainer/ScheduleBlockSummary'
import { ruleCardActions } from '../../../../../../../store/slices/ruleCardSlice'
import {
    addItemToArrayEnd,
    deleteRuleBlockArrayCardItem,
    duplicateRuleArrayCardItem,
    modifyRuleInput,
} from '../../../../../../../store/actions/ruleCardActions'
import { transformDate } from '../../../../../DateTime/utils'
import { TimeFormats } from '../../../../../DateTime/types'
import {
    DAYS_OF_WEEK,
    FROM,
    PluginSchedule,
    TimePeriod,
    TO,
} from '../../../SchedulesContainer/lib/PluginSchedule'

interface Props {
    scheduleSchema: any
    schedule: PluginSchedule
    schedules: PluginSchedule[]
    index: number
    dataPath: string[]
    pathWithElementID: string | undefined
    inCard?: boolean
    isRuleActive?: boolean
}

export const RuleScheduleBlock = ({
    scheduleSchema,
    schedule,
    schedules,
    index,
    dataPath,
    pathWithElementID,
    inCard,
    isRuleActive,
}: Props) => {
    const dispatch = useAppDispatch()

    const localId = schedule.element_id
    const ariaID = generateAriaId()
    const path = [...dataPath, index] as unknown as string[]

    const { expanded, expandBtn, toggle } = useExpand({
        isExpanded: true,
        uniqueID: localId,
        ariaID,
        className: `${styles.button} alwaysActive`,
    })

    const title = scheduleSchema.name ? scheduleSchema.name : 'Schedule'

    const inactiveRuleStyles =
        inCard && !isRuleActive
            ? 'rule-inactive-block'
            : 'block-bg-header-default'

    const styleHeader = [styles.header, inactiveRuleStyles, 'rounded-top']

    const errorPathWithElementID = pathWithElementID
        ? [
              ...(pathWithElementID?.split(PATH_JOINER) ?? []),
              schedule.element_id,
          ]
        : [...(dataPath ?? []), schedule.element_id]

    const errorKeyName = errorPathWithElementID.join(PATH_JOINER)

    const handleDelete = () => {
        const filteredSchedules = schedules.filter(
            (item) => item.element_id !== schedule.element_id
        )
        dispatch(
            deleteRuleBlockArrayCardItem({
                path: dataPath,
                data: filteredSchedules,
                errorKeyName,
            })
        )
    }

    const handleDuplicate = () => {
        const scheduleClone = _cloneDeep(schedule)
        scheduleClone.element_id = generateElementID()
        dispatch(
            duplicateRuleArrayCardItem({
                path: dataPath,
                data: { index, item: scheduleClone },
            })
        )
    }

    const handleDateFromChange = (pathArr: string[], date: Date | null) => {
        const dateFromFormatted =
            transformDate(TimeFormats.DATE, date, TimeFormats.DATE) ?? ''

        dispatch(
            modifyRuleInput({
                componentState: { [FROM]: dateFromFormatted },
                path: pathArr,
                data: { [FROM]: dateFromFormatted },
                name: FROM,
            })
        )
    }

    const handleDateToChange = (pathArr: string[], date: Date | null) => {
        const dateToFormatted = date
            ? transformDate(TimeFormats.DATE, date, TimeFormats.DATE)
            : null
        dispatch(
            modifyRuleInput({
                componentState: { [TO]: dateToFormatted },
                path: pathArr,
                data: { [TO]: dateToFormatted },
                name: TO,
            })
        )
    }

    const handleDayOfWeekChange = (pathArr: string[], days: any) => {
        dispatch(
            modifyRuleInput({
                componentState: { [DAYS_OF_WEEK]: days },
                path: pathArr,
                data: { [DAYS_OF_WEEK]: days },
                name: DAYS_OF_WEEK,
            })
        )
    }

    const handleAddTime = (pathArr: string[], data: any) => {
        dispatch(addItemToArrayEnd({ path: pathArr, data }))
    }

    const handleDeleteTime = (
        pathArr: string[],
        data: TimePeriod[],
        errKey: string
    ) => {
        dispatch(
            deleteRuleBlockArrayCardItem({
                path: pathArr,
                data,
                errorKeyName: errKey,
            })
        )
    }

    const handleStartTimeChange = (pathArr: string[], time: string) => {
        dispatch(
            modifyRuleInput({
                componentState: { [FROM]: time },
                path: pathArr,
                data: { [FROM]: time },
                name: FROM,
            })
        )
    }

    const handleEndTimeChange = (pathArr: string[], time: string | null) => {
        dispatch(
            modifyRuleInput({
                componentState: { [TO]: time },
                path: pathArr,
                data: { [TO]: time },
                name: TO,
            })
        )
    }

    const hasInvalidFields = !!schedules.reduce((acc, schedule) => {
        const daysOfWeekValues = Object.values(schedule.days_of_week)
        const isAllDaysFalse = daysOfWeekValues.every((day) => !day)
        if (isAllDaysFalse) {
            acc += 1
        }
        return acc
    }, 0)

    useEffect(() => {
        if (hasInvalidFields) {
            dispatch(ruleCardActions.setRuleConfigInvalid({ errorKeyName }))
        }
        if (!hasInvalidFields) {
            dispatch(ruleCardActions.setRuleConfigValid({ errorKeyName }))
        }
    }, [hasInvalidFields, dispatch, errorKeyName])

    return (
        <div className="mw-350 d-flex flex-column rounded border mt-2 mb-2 position-relative align-self-start">
            <div
                className={`d-flex align-items-center justify-content-between pointer w-100 rounded-top ${styleHeader.join(
                    ' '
                )}`}
            >
                <div
                    className="w-100"
                    onClick={toggle}
                >
                    <h4 className={styles.title}>{title}</h4>
                </div>
                <DuplicateBtn
                    className={`${styles.button} me-0`}
                    onClick={handleDuplicate}
                />
                <AppButton
                    variant="delete"
                    onClick={handleDelete}
                    tooltip="tooltips.delete"
                    className={`${styles.button} me-0`}
                />
                {expandBtn}
            </div>
            <Collapse in={expanded}>
                <div className="p-relative">
                    <div className="d-flex flex-column gap-3 py-4 px-2 w-100 ">
                        <ScheduleDates
                            period={schedule?.date_period}
                            dataPath={path}
                            onDateToChange={handleDateToChange}
                            onDateFromChange={handleDateFromChange}
                        />
                        <ScheduleDaysOfWeek
                            schema={scheduleSchema.days_of_week}
                            daysOfWeek={schedule?.days_of_week}
                            dataPath={path}
                            onDaysOfWeekChange={handleDayOfWeekChange}
                            isRuleActive={isRuleActive}
                        />
                        <ScheduleTimes
                            schema={scheduleSchema.time_periods}
                            timePeriods={schedule?.time_periods}
                            dataPath={path}
                            onTimeAdd={handleAddTime}
                            onTimeDelete={handleDeleteTime}
                            onStartTimeChange={handleStartTimeChange}
                            onEndTimeChange={handleEndTimeChange}
                        />
                        <ScheduleBlockSummary
                            schedule={schedule}
                            isRuleActive={isRuleActive}
                        />
                    </div>
                </div>
            </Collapse>
        </div>
    )
}
